// Design token names (plan 8.1.2): the CSS variables declared in ./tokens.css, grouped the way the plan describes them.
// The values live only in tokens.css (light default → system dark → data-theme override, three blocks); code refers to a
// token by name, e.g. style={{ color: cssVar('--dash-text-muted') }}. The tests check that this list and the CSS agree.

/** Lucian UX/UI original tokens (MIT © 2026 Seokwoooo) — values are never changed, only added to. */
export const UX_TOKENS = [
  '--ux-page',
  '--ux-surface',
  '--ux-surface-2',
  '--ux-surface-3',
  '--ux-ink',
  '--ux-ink-2',
  '--ux-ink-3',
  '--ux-line',
  '--ux-line-2',
  '--ux-accent',
  '--ux-accent-2',
  '--ux-accent-soft',
  '--ux-accent-ink',
  '--ux-mint',
  '--ux-warn',
  '--ux-danger',
  '--ux-glass',
  '--ux-glass-line',
  '--ux-shadow',
  '--ux-shadow-strong',
  '--ux-grad',
  '--ux-ease',
  '--ux-spring',
] as const;

/** Shell values carried over from the gallery app (tile, glow, bar glass, grain, rail glass, max content width). */
export const G_TOKENS = ['--g-tile', '--g-glow', '--g-bar', '--g-grain', '--g-rail', '--g-max'] as const;

/** Dashboard extension: layout sizes, fonts and the contrast-corrected text colours (plan 8.1.3). */
export const DASH_TOKENS = [
  '--dash-font',
  '--dash-mono',
  '--dash-rail-w',
  '--dash-rail-w-narrow',
  '--dash-header-h',
  '--dash-gutter',
  '--dash-radius-card',
  '--dash-radius-ctl',
  '--dash-header-bg',
  '--dash-text-muted',
  '--dash-text-accent',
  '--dash-text-info',
  '--dash-text-ok',
  '--dash-text-warn',
  '--dash-text-danger',
] as const;

/** Tool palette: the same colour for a tool on every screen (CLI tool = fill, API key call = hatching of the same colour). */
export const CAT_TOKENS = ['--cat-claude', '--cat-codex', '--cat-anthropic-api', '--cat-openai-api', '--cat-other'] as const;

/** Non-tool comparisons (model, team, thinking depth, …): one teal ramp, plus the single-series colour and the previous-period grey. */
export const SERIES_TOKENS = ['--series-1', '--series-2', '--series-3', '--series-4', '--series-5', '--series-neutral', '--cmp-prev'] as const;

/** v0 aliases kept by the mockup (s1 Claude Code · s2 Codex · s3 Anthropic API · s4 OpenAI API · s5 other · s6 comparison). */
export const LEGACY_SERIES_TOKENS = ['--dash-s1', '--dash-s2', '--dash-s3', '--dash-s4', '--dash-s5', '--dash-s6'] as const;

export const TOKEN_NAMES = [...UX_TOKENS, ...G_TOKENS, ...DASH_TOKENS, ...CAT_TOKENS, ...SERIES_TOKENS, ...LEGACY_SERIES_TOKENS] as const;
export type TokenName = (typeof TOKEN_NAMES)[number];

/** Tokens that the dark theme redefines (system dark media query and [data-theme="dark"] carry the same set). */
export const DARK_TOKENS = [
  ...UX_TOKENS.filter((n) => n !== '--ux-ease' && n !== '--ux-spring'),
  '--g-tile',
  '--g-glow',
  '--g-bar',
  '--g-grain',
  '--g-rail',
  '--dash-header-bg',
  '--dash-text-muted',
  '--dash-text-accent',
  '--dash-text-info',
  '--dash-text-ok',
  '--dash-text-warn',
  '--dash-text-danger',
  ...CAT_TOKENS,
  ...SERIES_TOKENS,
] as const satisfies readonly TokenName[];

/** `var(--name)` for inline styles and SVG fills. */
export const cssVar = (name: TokenName, fallback?: string): string => (fallback ? `var(${name},${fallback})` : `var(${name})`);

/** Usage source key (contracts SOURCES) → tool colour. Charts hatch the API sources with the same colour. */
export const TOOL_COLOR = {
  'cli.claude_code': cssVar('--cat-claude'),
  'cli.codex': cssVar('--cat-codex'),
  'api.anthropic': cssVar('--cat-anthropic-api'),
  'api.openai': cssVar('--cat-openai-api'),
  other: cssVar('--cat-other'),
} as const;

/** Comparison palette in drawing order (charts.js PALETTE): five teal steps, then grey for "other". */
export const SERIES_PALETTE = [
  cssVar('--series-1'),
  cssVar('--series-2'),
  cssVar('--series-3'),
  cssVar('--series-4'),
  cssVar('--series-5'),
  cssVar('--cat-other'),
] as const;

export const PREV_PERIOD_COLOR = cssVar('--cmp-prev');
export const SINGLE_SERIES_COLOR = cssVar('--series-neutral');

/** Responsive breakpoints from the reference originals (plan 8.3): phone ≤ 760, tablet ≤ 1099, small phone ≤ 380. */
export const BREAKPOINTS = { phone: 760, tablet: 1099, smallPhone: 380, headerCompact: 1439, headerNoLang: 1279, headerTight: 420 } as const;
